import { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  shouldShowCallBanner,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
} from '../data/contact';
import './Layout.css';

function Layout() {
  const { isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showCallBanner = shouldShowCallBanner(pathname);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifTooltip, setShowNotifTooltip] = useState(false);
  const [showMainMenu, setShowMainMenu] = useState(false);
  const notifHoverTimer = useRef<number | null>(null);
  const profileDropdownRef = useRef<HTMLDivElement | null>(null);
  const mainMenuRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = () => {
    logout();
    setShowProfileDropdown(false);
    navigate('/');
  };

  const handleMenuClick = (path: string) => {
    setShowProfileDropdown(false);
    navigate(path);
  };

  const handleMainMenuNavigate = (path: string) => {
    setShowMainMenu(false);
    navigate(path);
  };

  const handleNotifMouseEnter = () => {
    if (notifHoverTimer.current) {
      clearTimeout(notifHoverTimer.current);
    }
    notifHoverTimer.current = window.setTimeout(() => setShowNotifTooltip(true), 600);
  };

  const handleNotifMouseLeave = () => {
    if (notifHoverTimer.current) {
      clearTimeout(notifHoverTimer.current);
      notifHoverTimer.current = null;
    }
    setShowNotifTooltip(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (profileDropdownRef.current && !profileDropdownRef.current.contains(target)) {
        setShowProfileDropdown(false);
      }

      if (mainMenuRef.current && !mainMenuRef.current.contains(target)) {
        setShowMainMenu(false);
      }
    };

    const handleScroll = () => {
      setShowProfileDropdown(false);
      setShowMainMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="layout">
      <div className="site-top">
        <header className="header">
          <div className="container">
            <div className="header-content">
              <Link
                to="/"
                className="logo"
                style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
              >
                <h1>GetSkimp</h1>
              </Link>

            <nav className="nav">
              <div className="nav-auth-desktop">
                {isAuthenticated ? (
                  <>
                    <Link to="/insurance/requests">My Requests</Link>

                    <div className="profile-dropdown-container" ref={profileDropdownRef}>
                      <button
                        className="profile-btn"
                        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                        title="Account menu"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        <svg
                          width="16"
                          height="12"
                          viewBox="0 0 16 12"
                          aria-hidden="true"
                          focusable="false"
                          style={{ display: 'block' }}
                        >
                          <rect x="0" y="0" width="16" height="2" rx="1" fill="currentColor"></rect>
                          <rect x="0" y="5" width="16" height="2" rx="1" fill="currentColor"></rect>
                          <rect x="0" y="10" width="16" height="2" rx="1" fill="currentColor"></rect>
                        </svg>
                        <span>Profile</span>
                      </button>

                      {showProfileDropdown && (
                        <div className="profile-dropdown">
                          <a
                            href="/settings"
                            onClick={(e) => {
                              e.preventDefault();
                              handleMenuClick('/settings');
                            }}
                          >
                            Settings
                          </a>
                          <a
                            href="/insurance/requests"
                            onClick={(e) => {
                              e.preventDefault();
                              handleMenuClick('/insurance/requests');
                            }}
                          >
                            My Requests
                          </a>
                          <span
                            aria-disabled="true"
                            onMouseEnter={handleNotifMouseEnter}
                            onMouseLeave={handleNotifMouseLeave}
                            style={{
                              color: '#9CA3AF',
                              cursor: 'not-allowed',
                              display: 'block',
                              padding: '0.75rem 1rem',
                              position: 'relative',
                            }}
                            title="Coming soon"
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                              <span aria-hidden="true" role="img">🔒</span>
                              Notifications
                            </span>
                            {showNotifTooltip && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: '100%',
                                  transform: 'translate(-8px, -50%)',
                                  background: '#111827',
                                  color: '#ffffff',
                                  padding: '6px 8px',
                                  borderRadius: '6px',
                                  fontSize: '0.8rem',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                  zIndex: 10,
                                }}
                              >
                                Coming soon
                              </div>
                            )}
                          </span>
                          <div className="dropdown-divider"></div>
                          <button className="logout-btn" onClick={handleLogout}>
                            Log Out
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="btn btn-sm header-login-btn">
                      Login
                    </Link>
                    <Link to="/register" className="btn btn-sm header-signup-btn">
                      Sign Up
                    </Link>
                  </>
                )}
              </div>

              <div className="header-menu-container" ref={mainMenuRef}>
                <button
                  type="button"
                  className="header-menu-button"
                  onClick={() => setShowMainMenu((prev) => !prev)}
                  aria-label="Open menu"
                  aria-haspopup="true"
                  aria-expanded={showMainMenu}
                >
                  <div className="header-menu-icon">
                    <span className="header-menu-icon-bar" />
                    <span className="header-menu-icon-bar" />
                  </div>
                </button>
                {showMainMenu && (
                  <div className="header-menu-dropdown">
                    <p className="header-menu-label">Account</p>
                    {isAuthenticated ? (
                      <>
                        <button type="button" onClick={() => handleMainMenuNavigate('/insurance/requests')}>
                          My Requests
                        </button>
                        <button type="button" onClick={() => handleMainMenuNavigate('/settings')}>
                          Settings
                        </button>
                        <button
                          type="button"
                          className="header-menu-logout"
                          onClick={() => {
                            setShowMainMenu(false);
                            handleLogout();
                          }}
                        >
                          Log Out
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => handleMainMenuNavigate('/login')}>
                          Login
                        </button>
                        <button type="button" onClick={() => handleMainMenuNavigate('/register')}>
                          Sign Up
                        </button>
                      </>
                    )}
                    <div className="header-menu-divider" />
                    <p className="header-menu-label">Resources</p>
                    <button type="button" onClick={() => handleMainMenuNavigate('/knowledge-base')}>
                      Support Articles
                    </button>
                    <button type="button" onClick={() => handleMainMenuNavigate('/help')}>
                      Support Center
                    </button>
                    <div className="header-menu-divider" />
                    <button type="button" onClick={() => handleMainMenuNavigate('/about')}>
                      About
                    </button>
                    <button type="button" onClick={() => handleMainMenuNavigate('/referral')}>
                      Referral Program
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>
        {showCallBanner && (
          <div className="call-banner" role="region" aria-label="Call GetSkimp">
            <div className="container call-banner-inner">
              <a className="call-banner-link" href={SUPPORT_PHONE_TEL}>
                Questions? Call us: {SUPPORT_PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}
      </div>

      <main className="main-content">
        <Outlet />
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2026 GetSkimp. Insurance made easy for Texans.</p>
            <div className="footer-links">
              <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_DISPLAY}</a>
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                Terms
              </a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
              <a href="/help" target="_blank" rel="noopener noreferrer">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
